import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useSpring } from 'framer-motion';
import { useFinePointer } from '@/lib/experience';
import { Paw } from '../brand/Shapes';

const STEP = 70; // distância (px) entre uma patinha e outra
const MAX_PAWS = 14;

/**
 * Cursor fofo (só desktop): bolinha azul que segue o mouse, vira balãozinho rosa
 * com rótulo sobre elementos com data-cursor e deixa um rastro de patinhas.
 */
export const Cursor = () => {
  const fine = useFinePointer();
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 450, damping: 28, mass: 0.5 });
  const sy = useSpring(y, { stiffness: 450, damping: 28, mass: 0.5 });
  const [label, setLabel] = useState(null);
  const [hoverLink, setHoverLink] = useState(false);
  const [visible, setVisible] = useState(false);
  const [paws, setPaws] = useState([]);
  const last = useRef(null);
  const side = useRef(1);

  useEffect(() => {
    if (!fine) return undefined;
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setVisible(true);
      const p = last.current;
      if (!p) {
        last.current = { x: e.clientX, y: e.clientY };
        return;
      }
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (Math.hypot(dx, dy) < STEP) return;
      const angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
      const nx = -dy / Math.hypot(dx, dy);
      const ny = dx / Math.hypot(dx, dy);
      side.current *= -1;
      const id = Math.random();
      setPaws((list) => [
        ...list.slice(-MAX_PAWS + 1),
        { id, x: e.clientX + nx * 9 * side.current, y: e.clientY + ny * 9 * side.current, angle },
      ]);
      setTimeout(() => setPaws((list) => list.filter((q) => q.id !== id)), 1200);
      last.current = { x: e.clientX, y: e.clientY };
    };
    const over = (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const withLabel = target?.closest('[data-cursor]');
      setLabel(withLabel ? withLabel.getAttribute('data-cursor') : null);
      setHoverLink(!withLabel && !!target?.closest('a, button, [role="button"], input[type="range"]'));
    };
    const leave = () => setVisible(false);
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerover', over, { passive: true });
    document.documentElement.addEventListener('pointerleave', leave);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerover', over);
      document.documentElement.removeEventListener('pointerleave', leave);
    };
  }, [fine, x, y]);

  if (!fine) return null;

  const size = label ? 92 : hoverLink ? 46 : 16;

  return (
    <>
      {/* rastro de patinhas */}
      <div className="pointer-events-none fixed inset-0 z-[129]" aria-hidden="true">
        <AnimatePresence>
          {paws.map((p) => (
            <motion.span
              key={p.id}
              className="absolute w-4 h-4 -ml-2 -mt-2 text-pet-pink-deep"
              style={{ left: p.x, top: p.y, rotate: p.angle }}
              initial={{ opacity: 0.55, scale: 0.4 }}
              animate={{ opacity: 0.45, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.35 }}
            >
              <Paw className="w-full h-full" />
            </motion.span>
          ))}
        </AnimatePresence>
      </div>

      <motion.div aria-hidden="true" className="pointer-events-none fixed left-0 top-0 z-[130]" style={{ x: sx, y: sy }}>
        <motion.div
          className={`-translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center ${
            label ? 'bg-pet-pink text-pet-pink-ink shadow-lg' : hoverLink ? 'border-[2.5px] border-pet-blue bg-pet-blue/10' : 'bg-pet-blue'
          }`}
          animate={{ width: size, height: size, opacity: visible ? 1 : 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 18 }}
        >
          <AnimatePresence>
            {label && (
              <motion.span
                key={label}
                className="font-display text-[15px] leading-none whitespace-nowrap"
                initial={{ opacity: 0, scale: 0.4, rotate: -20 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.4 }}
                transition={{ type: 'spring', stiffness: 500, damping: 15 }}
              >
                {label}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </>
  );
};

export default Cursor;
